import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { scheduleService } from "../../../../api/enhancedApi";
import { emailTemplateApi } from "../../../../api/emailTemplateApi";
import { useAuth } from "../../../../hooks/useAuth";
import { hasIngestSource } from "../../AirlineScheduleIngest/ingestListPresentation";

/**
 * 计划状态操作的自定义Hook
 * 封装所有状态变更和操作的逻辑
 */
export const useScheduleActions = (schedule, onStatusChange, refreshSchedules) => {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [isEmailLoading, setIsEmailLoading] = useState(false);
    const [isEmailEditorVisible, setIsEmailEditorVisible] = useState(false);
    const [emailTemplate, setEmailTemplate] = useState(null);
    const [confirmDrawerVisible, setConfirmDrawerVisible] = useState(false);
    const [publishDrawerVisible, setPublishDrawerVisible] = useState(false);
    const [comparePreviewVisible, setComparePreviewVisible] = useState(false);
    const [emailSending, setEmailSending] = useState(false);
    const [publishEmailDrawerVisible, setPublishEmailDrawerVisible] = useState(false);
    
    // 模板选择相关状态
    const [templateSelectionVisible, setTemplateSelectionVisible] = useState(false);
    const [selectedTemplateCode, setSelectedTemplateCode] = useState(null);
    
    // 统一邮件发送抽屉状态
    const [unifiedEmailDrawerVisible, setUnifiedEmailDrawerVisible] = useState(false);
    const [unifiedEmailType, setUnifiedEmailType] = useState('confirmation'); // 'confirmation' | 'publish'

    // 状态更新处理
    const handleStatusUpdate = useCallback(
        async (newStatus, successMessage) => {
            try {
                await scheduleService.updateScheduleStatus(schedule?.id, newStatus);
                message.success(successMessage);
                onStatusChange && onStatusChange();
                return true;
            } catch (error) {
                message.error(`状态更新失败: ${error.message || "未知错误"}`);
                return false;
            }
        },
        [schedule?.id, onStatusChange],
    );

    // 邮件编辑器相关
    const handleOpenEmailEditor = useCallback(async (templateCode) => {
        try {
            setIsEmailLoading(true);
            // 使用传入的模板代码或默认的更新确认模板
            const templateCodeToUse = templateCode || "SCHEDULE_AAE_UPDATE_CONFIRMATION";
            const templateResponse = await emailTemplateApi.getTemplateByCodeWithRecipients(templateCodeToUse);

            if (templateResponse.data) {
                setEmailTemplate(templateResponse.data);
                setSelectedTemplateCode(templateCodeToUse);
                console.log("[useScheduleActions] 已加载邮件模板和收件人配置:", templateResponse.data);
            }

            setIsEmailEditorVisible(true);
        } catch (error) {
            console.error("获取邮件模板失败:", error);
            message.error(`获取邮件模板失败: ${error.message || "未知错误"}`);
        } finally {
            setIsEmailLoading(false);
        }
    }, []);

    // 显示模板选择对话框
    const handleSendConfirmationEmail = useCallback(() => {
        setTemplateSelectionVisible(true);
    }, []);

    // 处理模板选择
    const handleTemplateSelection = useCallback((templateCode) => {
        setTemplateSelectionVisible(false);
        handleOpenEmailEditor(templateCode);
    }, [handleOpenEmailEditor]);

    // 取消模板选择
    const handleCancelTemplateSelection = useCallback(() => {
        setTemplateSelectionVisible(false);
    }, []);

    // 统一邮件发送抽屉处理函数
    const handleOpenUnifiedEmailDrawer = useCallback((emailType = 'confirmation') => {
        setUnifiedEmailType(emailType);
        setUnifiedEmailDrawerVisible(true);
    }, []);

    const handleCloseUnifiedEmailDrawer = useCallback(() => {
        setUnifiedEmailDrawerVisible(false);
    }, []);

    const handleUnifiedEmailSuccess = useCallback(() => {
        setUnifiedEmailDrawerVisible(false);
        // 刷新计划状态
        if (refreshSchedules) {
            refreshSchedules();
        }
        // 触发状态变化回调
        if (onStatusChange) {
            onStatusChange();
        }
    }, [refreshSchedules, onStatusChange]);

    // 快速发送确认邮件（跳过抽屉，直接进入邮件编辑器）
    const handleQuickSendConfirmationEmail = useCallback((templateCode = "SCHEDULE_AAE_NEW_CONFIRMATION") => {
        setUnifiedEmailType('confirmation');
        setUnifiedEmailDrawerVisible(true);
        // 注意：快速模式的参数会在组件中处理
    }, []);

    // 快速发送发布邮件（跳过抽屉，直接进入邮件编辑器）
    const handleQuickSendPublishEmail = useCallback((templateCode = "SCHEDULE_OUTSTATION_NEW_PUBLICATION") => {
        setUnifiedEmailType('publish');
        setUnifiedEmailDrawerVisible(true);
        // 注意：快速模式的参数会在组件中处理
    }, []);

    // 跳过邮件直接确认
    const handleSkipEmailConfirm = useCallback(() => {
        setConfirmDrawerVisible(true);
    }, []);

    // 手动确认（从CONFIRMATION_PENDING状态）
    const handleManualConfirm = useCallback(async () => {
        await handleStatusUpdate("AIRLINE_CONFIRMED", "计划已手动确认");
    }, [handleStatusUpdate]);

    // 确认成功回调
    const handleConfirmSuccess = useCallback(() => {
        setConfirmDrawerVisible(false);
        onStatusChange && onStatusChange();
    }, [onStatusChange]);

    // 发布
    const handlePublish = useCallback(() => {
        setPublishDrawerVisible(true);
    }, []);

    // 发布成功回调
    const handlePublishSuccess = useCallback(() => {
        setPublishDrawerVisible(false);
        onStatusChange && onStatusChange();
    }, [onStatusChange]);

    // 对比预览
    const handleComparePreview = useCallback(() => {
        setComparePreviewVisible(true);
    }, []);

    // 发送发布邮件
    const handleSendPublishEmail = useCallback(() => {
        setPublishEmailDrawerVisible(true);
    }, []);

    // 发送发布邮件成功回调
    const handlePublishEmailSuccess = useCallback(() => {
        setPublishEmailDrawerVisible(false);
        // 发送发布邮件不改变状态，只刷新数据
        refreshSchedules && refreshSchedules();
    }, [refreshSchedules]);

    // 废弃
    const handleDeprecate = useCallback(async () => {
        await handleStatusUpdate("DEPRECATED", "计划已废弃");
    }, [handleStatusUpdate]);

    // 回退操作
    const handleRevertToDraft = useCallback(async () => {
        const ingestOrigin = hasIngestSource(schedule);
        const canEdit = hasPermission("schedule:update");
        const canReadDrafts = hasPermission("schedule:read");
        let successMessage = "已回退到草稿状态";
        if (ingestOrigin && canEdit) {
            successMessage = "已回退到草稿，正在打开编辑器";
        } else if (ingestOrigin && canReadDrafts) {
            successMessage = "已回退到草稿。接入工作面不再列出这份计划，请到草稿列表继续修改";
        } else if (ingestOrigin) {
            successMessage = "已回退到草稿。接入工作面不再列出这份计划";
        }
        const updated = await handleStatusUpdate("DRAFT", successMessage);
        if (updated && ingestOrigin && canEdit && schedule?.id) {
            navigate(`/flightSchedule/editor/${schedule.id}`);
        }
    }, [handleStatusUpdate, schedule, hasPermission, navigate]);

    const handleEditDraft = useCallback(() => {
        if (!schedule?.id) return;
        navigate(`/flightSchedule/editor/${schedule.id}`);
    }, [navigate, schedule?.id]);

    const handleRevertToSubmitted = useCallback(async () => {
        await handleStatusUpdate("SUBMITTED", "已回退到已提交状态");
    }, [handleStatusUpdate]);

    const handleRevertToConfirmationPending = useCallback(async () => {
        await handleStatusUpdate("CONFIRMATION_PENDING", "已回退到待确认状态");
    }, [handleStatusUpdate]);

    const handleRevertToAirlineConfirmed = useCallback(async () => {
        try {
            if (schedule?.status === "PUBLISHED") {
                // 如果是已发布状态，使用取消发布API
                message.loading("正在取消发布...", 0);
                const response = await scheduleService.unpublishSchedule(schedule.id);
                message.destroy();
                const notices = response?.data?.result?.executed_route_notices;
                if (Array.isArray(notices) && notices.length > 0) {
                    message.warning(`计划已回退到航司确认。${notices.join("；")}`);
                } else {
                    message.success("计划已成功回退到航司确认状态");
                }
            } else {
                // 其他状态使用普通状态更新API
                await scheduleService.updateScheduleStatus(schedule.id, "AIRLINE_CONFIRMED");
                message.success("已回退到航司确认状态");
            }
            onStatusChange && onStatusChange();
        } catch (error) {
            message.destroy();
            const detail = error?.response?.data?.detail;
            const text = typeof detail === "string" ? detail : error.message;
            message.error(`回退计划失败: ${text || "未知错误"}`);
            console.error("回退计划失败:", error);
        }
    }, [schedule, onStatusChange]);

    const handleEmailSubmit = async (emailData) => {
        setEmailSending(true);
        try {
            // 动态导入邮件API - 使用新的V2 API
            const { sendScheduleConfirmationEmailV2 } = await import("../../../../api/scheduleEmailApi");

            // 转换前端数据格式为后端期望的ScheduleConfirmationEmailRequest格式
            // 处理邮件地址：数组转字符串，空数组转null
            const convertEmailField = (field) => {
                if (Array.isArray(field)) {
                    return field.length > 0 ? field.join(", ") : null;
                }
                return field || null;
            };

            const requestData = {
                to: convertEmailField(emailData.to) || "", // to字段必须有值
                cc: convertEmailField(emailData.cc),
                bcc: convertEmailField(emailData.bcc),
                subject: emailData.subject || "",
                rendered_html: emailData.contentHtml || "",
                content_block_context: emailData.contentBlockContext || {
                    template_code: selectedTemplateCode || "SCHEDULE_AAE_UPDATE_CONFIRMATION",
                    instances: [],
                    business_context_snapshot: {},
                },
                attachments: emailData.attachments || [],
            };

            // 使用新的V2 API，返回标准的 {success, message, data} 格式
            const response = await sendScheduleConfirmationEmailV2(schedule.id, requestData);

            // 根据API规范，检查业务操作结果
            if (response.success) {
                message.success(response.message || "确认邮件发送成功");

                // 刷新计划状态
                if (refreshSchedules) {
                    await refreshSchedules();
                }

                // 触发状态变化回调，确保抽屉内部数据也更新
                if (onStatusChange) {
                    await onStatusChange();
                }

                // 关闭邮件编辑器
                setIsEmailEditorVisible(false);
            } else {
                message.error(response.message || "邮件发送失败");
            }
        } catch (error) {
            console.error("发送邮件失败:", error);

            // 安全地提取错误消息，确保是字符串
            let errorMessage = "发送邮件时发生错误";

            if (error.response?.data?.detail) {
                // 如果detail是数组（Pydantic验证错误），提取并格式化错误消息
                if (Array.isArray(error.response.data.detail)) {
                    const validationErrors = error.response.data.detail
                        .map((err) => {
                            const field = err.loc ? err.loc.join(".") : "未知字段";
                            return `${field}: ${err.msg}`;
                        })
                        .join("; ");
                    errorMessage = `数据验证失败: ${validationErrors}`;
                } else if (typeof error.response.data.detail === "string") {
                    errorMessage = error.response.data.detail;
                }
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            message.error(errorMessage);
        } finally {
            setEmailSending(false);
        }
    };

    const handleEmailCancel = useCallback(() => {
        setIsEmailEditorVisible(false);
    }, []);

    // 返回所有状态和处理函数
    return {
        // 状态
        isEmailLoading,
        isEmailEditorVisible,
        emailTemplate,
        confirmDrawerVisible,
        publishDrawerVisible,
        comparePreviewVisible,
        emailSending,
        publishEmailDrawerVisible,
        templateSelectionVisible,
        selectedTemplateCode,
        unifiedEmailDrawerVisible,
        unifiedEmailType,

        // 邮件相关
        handleOpenEmailEditor,
        handleEmailSubmit,
        handleEmailCancel,

        // 模板选择相关
        handleTemplateSelection,
        handleCancelTemplateSelection,

        // 状态操作
        handleSendConfirmationEmail,
        handleSkipEmailConfirm,
        handleManualConfirm,
        handleConfirmSuccess,
        handlePublish,
        handlePublishSuccess,
        handleComparePreview,
        handleDeprecate,
        handleSendPublishEmail,
        handlePublishEmailSuccess,
        handleEditDraft,

        // 回退操作
        handleRevertToDraft,
        handleRevertToSubmitted,
        handleRevertToConfirmationPending,
        handleRevertToAirlineConfirmed,

        // 统一邮件发送抽屉
        handleOpenUnifiedEmailDrawer,
        handleCloseUnifiedEmailDrawer,
        handleUnifiedEmailSuccess,

        // 快速邮件发送
        handleQuickSendConfirmationEmail,
        handleQuickSendPublishEmail,

        // 设置函数
        setConfirmDrawerVisible,
        setPublishDrawerVisible,
        setComparePreviewVisible,
        setPublishEmailDrawerVisible,
        setUnifiedEmailDrawerVisible,
    };
};
